// Folder utility functions and constants

// Error messages mapping
export const FOLDER_ERROR_MESSAGES = {
  "Cannot create root folders": "All folders must be created under your workspace",
  "Cannot delete your main workspace folder": "Your main workspace cannot be deleted", 
  "Cannot move your main workspace folder": "Your main workspace cannot be moved",
  "Folder with this name already exists": "A folder with this name already exists here",
  "Cannot create folder": "Failed to create folder",
  "Cannot update folder": "Failed to update folder",
  "Cannot delete folder": "Failed to delete folder",
  "Cannot move folder": "Failed to move folder"
};

// Folder validation rules
export const FOLDER_VALIDATION = {
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 500,
  MAX_NESTING_LEVEL: 10
};

// Folder colors
export const FOLDER_COLORS = [
  { name: 'Blue', value: '#3498db' },
  { name: 'Green', value: '#2ecc71' },
  { name: 'Purple', value: '#9b59b6' },
  { name: 'Orange', value: '#e67e22' },
  { name: 'Red', value: '#e74c3c' },
  { name: 'Teal', value: '#1abc9c' },
  { name: 'Yellow', value: '#f1c40f' },
  { name: 'Gray', value: '#95a5a6' }
];

/**
 * Check if a folder is a root folder
 * @param {string} folderId - The folder ID to check
 * @param {string} userId - The user ID
 * @returns {boolean} True if the folder is a root folder
 */
export const isRootFolder = (folderId, userId) => {
  return folderId === userId;
};

/**
 * Get friendly error message for API errors
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getFriendlyErrorMessage = (error) => {
  const apiMessage = error.response?.data?.message || error.message;
  return FOLDER_ERROR_MESSAGES[apiMessage] || apiMessage || "An unexpected error occurred";
};

/**
 * Validate folder name
 * @param {string} name - The folder name to validate
 * @returns {object} Validation result with isValid and error message
 */
export const validateFolderName = (name) => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, error: 'Folder name is required' };
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length < FOLDER_VALIDATION.NAME_MIN_LENGTH) {
    return { isValid: false, error: 'Folder name cannot be empty' };
  }

  if (trimmedName.length > FOLDER_VALIDATION.NAME_MAX_LENGTH) {
    return { isValid: false, error: `Folder name cannot exceed ${FOLDER_VALIDATION.NAME_MAX_LENGTH} characters` };
  }

  // Check for invalid characters
  const invalidChars = /[<>:"/\\|?*]/;
  if (invalidChars.test(trimmedName)) {
    return { isValid: false, error: 'Folder name contains invalid characters' };
  }

  return { isValid: true, error: null };
};

/**
 * Validate folder description
 * @param {string} description - The folder description to validate
 * @returns {object} Validation result with isValid and error message
 */
export const validateFolderDescription = (description) => {
  if (!description) {
    return { isValid: true, error: null }; // Description is optional
  }

  if (typeof description !== 'string') {
    return { isValid: false, error: 'Description must be a string' };
  }

  if (description.length > FOLDER_VALIDATION.DESCRIPTION_MAX_LENGTH) {
    return { isValid: false, error: `Description cannot exceed ${FOLDER_VALIDATION.DESCRIPTION_MAX_LENGTH} characters` };
  }

  return { isValid: true, error: null };
};

/**
 * Get folder path as string
 * @param {Array} pathArray - Array of folder objects representing the path
 * @returns {string} Path string like "/John Doe/Projects/React Apps"
 */
export const getFolderPathString = (pathArray) => {
  if (!Array.isArray(pathArray) || pathArray.length === 0) {
    return '/';
  }

  return '/' + pathArray.map(folder => folder.name).join('/');
};

/**
 * Find folder by ID in tree structure
 * @param {string} folderId - The folder ID to find
 * @param {Array} folders - Array of folder objects to search in
 * @returns {object|null} The found folder object or null
 */
export const findFolderById = (folderId, folders = []) => {
  for (const folder of folders) {
    if (folder.id === folderId) {
      return folder;
    }
    if (folder.children && folder.children.length > 0) {
      const found = findFolderById(folderId, folder.children);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Get all descendant folder IDs
 * @param {object} folder - The parent folder
 * @returns {Array} Array of descendant folder IDs
 */
export const getDescendantIds = (folder) => {
  const ids = [];
  
  if (folder.children && folder.children.length > 0) {
    for (const child of folder.children) {
      ids.push(child.id);
      ids.push(...getDescendantIds(child));
    }
  }
  
  return ids;
};

/**
 * Check if folder can be moved to target parent
 * @param {string} folderId - The folder to move
 * @param {string} targetParentId - The target parent folder
 * @param {Array} folderTree - The complete folder tree
 * @param {string} userId - The user ID
 * @returns {object} Result with canMove boolean and error message
 */
export const canMoveFolder = (folderId, targetParentId, folderTree, userId) => {
  // Cannot move root folder
  if (isRootFolder(folderId, userId)) {
    return { canMove: false, error: 'Cannot move your main workspace folder' };
  }

  // Cannot move to itself
  if (folderId === targetParentId) {
    return { canMove: false, error: 'Cannot move folder to itself' };
  }

  // Find the folder being moved
  const folder = findFolderById(folderId, folderTree);
  if (!folder) {
    return { canMove: false, error: 'Folder not found' };
  }

  // Cannot move to its own descendant
  const descendantIds = getDescendantIds(folder);
  if (descendantIds.includes(targetParentId)) {
    return { canMove: false, error: 'Cannot move folder to its own subfolder' };
  }

  return { canMove: true, error: null };
};

/**
 * Get folder actions based on folder type and permissions
 * @param {object} folder - The folder object
 * @param {string} userId - The user ID
 * @returns {Array} Array of available actions
 */
export const getFolderActions = (folder, userId) => {
  const isRoot = isRootFolder(folder.id, userId);
  
  if (isRoot) {
    return [
      { label: "Rename", action: "rename", enabled: true, icon: "edit" },
      { label: "Add Folder", action: "addChild", enabled: true, icon: "add" },
      { label: "Edit Description", action: "editDesc", enabled: true, icon: "description" },
      { label: "Delete", action: "delete", enabled: false, icon: "delete" },
      { label: "Move", action: "move", enabled: false, icon: "move" }
    ];
  } else {
    const hasChildren = folder.children && folder.children.length > 0;
    return [
      { label: "Rename", action: "rename", enabled: true, icon: "edit" },
      { label: "Add Folder", action: "addChild", enabled: true, icon: "add" },
      { label: "Edit Description", action: "editDesc", enabled: true, icon: "description" },
      { label: "Delete", action: "delete", enabled: !hasChildren, icon: "delete" },
      { label: "Move", action: "move", enabled: true, icon: "move" }
    ];
  }
};

/**
 * Sort folders alphabetically with root folder first
 * @param {Array} folders - Array of folders to sort
 * @param {string} userId - The user ID
 * @returns {Array} Sorted array of folders
 */
export const sortFolders = (folders, userId) => {
  return [...folders].sort((a, b) => {
    // Root folder always comes first
    const aIsRoot = isRootFolder(a.id, userId);
    const bIsRoot = isRootFolder(b.id, userId);
    
    if (aIsRoot && !bIsRoot) return -1;
    if (!aIsRoot && bIsRoot) return 1;
    
    // Sort alphabetically
    return a.name.localeCompare(b.name);
  });
};

/**
 * Get folder statistics
 * @param {object} folder - The folder object
 * @returns {object} Statistics object
 */
export const getFolderStats = (folder) => {
  let totalFolders = 0;
  let totalSnippets = folder.snippetCount || 0;
  let maxDepth = 0;

  const calculateStats = (f, depth = 0) => {
    maxDepth = Math.max(maxDepth, depth);
    
    if (f.children && f.children.length > 0) {
      totalFolders += f.children.length;
      
      for (const child of f.children) {
        totalSnippets += child.snippetCount || 0;
        calculateStats(child, depth + 1);
      }
    }
  };

  calculateStats(folder);

  return {
    totalFolders,
    totalSnippets,
    maxDepth,
    hasChildren: (folder.children && folder.children.length > 0)
  };
};
